'use client';

import dynamic from 'next/dynamic';
import InlineStyle from '@/components/InlineStyle';
import { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Check } from 'lucide-react';
import { BookDemoButton, FaqAccordion } from '@/components/ui';
import { MetricsBand } from '@/components/primitives/MetricsBand';
import { HeroCollage } from '@/components/primitives/HeroCollage';
import meStyles from '@/app/lp/customer-experience-platform-middle-east/me-lp.module.css';
import { ME_HERO_COLLAGE } from '@/app/lp/customer-experience-platform-middle-east/me-hero-collage';
import {
  fadeUp, slideInLeft, slideInRight, scaleIn, viewportOnce,
  spring, springSoft, staggerContainer,
} from '@/lib/motion';

const BookDemoSection = dynamic(() => import('@/components/BookDemoSection'));
const MeAICarousel = dynamic(() => import('@/app/lp/customer-experience-platform-middle-east/MeAICarousel'));
const MeOutcomesCarousel = dynamic(() => import('@/app/lp/customer-experience-platform-middle-east/MeOutcomesCarousel'));
const MeLogoMarquee = dynamic(() => import('@/app/lp/customer-experience-platform-middle-east/MeLogoMarquee'));
const MeTrustBanner = dynamic(() => import('@/app/lp/customer-experience-platform-middle-east/MeTrustBanner'));

/* ── Design tokens ──────────────────────────────────────────────────────── */
const BRAND = '#0098FF';
const BRAND_SUBTLE = 'rgba(0,152,255,0.08)';
const BRAND_BORDER = 'rgba(0,152,255,0.18)';
const BG_DARK = '#0D1B3E';
const IMG = '/wp-content/uploads/me-cx';

const FEATURE_ICONS = [
  'feature-ai-guided.svg',
  'feature-intelligent-query.svg',
  'feature-summarisation.svg',
  'feature-automation.svg',
  'feature-multilingual.svg',
  'feature-nps-survey.svg',
];

/* ── Content data ───────────────────────────────────────────────────────── */
const METRICS = [
  { value: '350+', label: 'علامة تجارية عالمية' },
  { value: '40B+', label: 'نقطة بيانات تمت معالجتها' },
  { value: '20M+', label: 'حالة عميل تم حلها' },
  { value: '35M+', label: 'عميل تم تحديد ملفاتهم الشخصية' },
  { value: '4', label: 'قارات يتم خدمتها' },
];

const STEPS = [
  {
    num: '01', title: 'الاستماع', img: 'step-listen.webp',
    desc: 'تتَّبع كل إشارة تخص علامتك التجارية عبر وسائل التواصل الاجتماعي ومنصات المراجعات والأخبار — باللغتين العربية والإنجليزية — من خلال الاستماع الاجتماعي في الوقت الفعلي. إذا كان عملاؤك يتحدثون، ستعلم على الفور.',
  },
  {
    num: '02', title: 'التفاعل', img: 'step-engage.webp',
    desc: 'استجب بشكل أسرع بفضل الأتمتة المدعومة بالذكاء الاصطناعي ورؤية 360° لكل عميل. قدِّم خدمة عملاء متعددة القنوات تبدو شخصية — لأنها كذلك فعلاً. يحلّ فريقك المزيد بجهد أقل.',
  },
  {
    num: '03', title: 'التحليل', img: 'step-analyse.webp',
    desc: 'حوِّل البيانات الخام إلى قرارات. افهم إشارات صوت العميل (VoC)، وتتَّبع اتجاهات المشاعر، واستخدم تحليلات وسائل التواصل الاجتماعي لتحديد ما يعمل — وما يجب إصلاحه بعد ذلك.',
  },
];

const SMART_CX_FEATURES = [
  { title: 'مساعدة الوكيل الموجَّهة بالذكاء الاصطناعي', desc: 'مساعد ذكاء اصطناعي في الوقت الفعلي يقلّل التخمين ويُبقي درجات CSAT مرتفعة.' },
  { title: 'تحديد أولوية الاستفسارات بذكاء', desc: 'يُبرز التفاعلات ويرتّبها حسب حالة VIP والإلحاحية والسياق — حتى لا تُغفَل القضايا الحرجة أبداً.' },
  { title: 'تلخيص آلي للتذاكر', desc: 'يحلّ الوكلاء الحالات بسرعة دون قراءة سلاسل المحادثات كاملةً.' },
  { title: 'أتمتة سير العمل', desc: 'يؤتمت الأسئلة الشائعة على نطاق واسع — حتى يركّز وكلاؤك على التفاعلات المعقّدة والحساسة.' },
  { title: 'دعم متعدد اللغات بسلاسة', desc: 'تفاعَل مع العملاء بأكثر من 40 لغة، بما في ذلك العربية ولهجاتها الإقليمية.' },
  { title: 'استطلاعات NPS آلية', desc: 'التقط ملاحظات صوت العميل (VoC) في اللحظة التي تهم.' },
].map((f, i) => ({ ...f, icon: FEATURE_ICONS[i] }));

const COMMAND_CENTRE_FEATURES = [
  { title: 'لوحة مراقبة مباشرة', desc: 'لوحات معلومات في الوقت الفعلي مصمّمة لقادة وفرق التسويق.' },
  { title: 'كشف الأزمات والاستجابة', desc: 'حدّد الأزمات خلال 90 ثانية وأبلغ فرق الشؤون القانونية أو العلاقات العامة أو تجربة العملاء فوراً — لاستجابة سريعة وتحكّم أدق وتقليل تأثير الأزمة على العلامة.' },
  { title: 'ذكاء تنافسي', desc: 'تتبّع المنافسين، وقارِن الأداء، واكتشف الفجوات.' },
  { title: 'تحسين الحملات', desc: 'حوِّل إشارات الجمهور إلى رؤى سريعة لتحسين الاستراتيجية وتعظيم عائد الاستثمار.' },
  { title: 'إدارة مواقع متعددة', desc: 'رؤى خاصة بكل منطقة لكل فرع أو منفذ — ضرورية للعلامات متعددة المواقع في الشرق الأوسط.' },
].map((f, i) => ({ ...f, icon: FEATURE_ICONS[i] }));

const SOCIAL_LISTENING_FEATURES = [
  { title: 'تغطية شاملة للقنوات', desc: 'من منتديات النقاش والأخبار إلى مواقع المراجعات ووسائل التواصل الاجتماعي — التقط كل إشارة في الوقت الفعلي.' },
  { title: 'تصنيف النية', desc: 'صنّف كل إشارة حسب الغرض: شكوى أو استفسار أو مديح أو ملاحظات — تلقائياً.' },
  { title: 'تحليل الجمهور', desc: 'افهم من هم عملاؤك وما الذي يحرّكهم.' },
  { title: 'تتبّع المؤثرين', desc: 'حدّد الأصوات التي تشكّل قرارات الشراء في سوقك.' },
  { title: 'لوحة العلاقات العامة', desc: 'حضور علامتك الإعلامي، في لمحة.' },
  { title: 'ذكاء الأداء', desc: 'حوِّل تحليلات وسائل التواصل الاجتماعي إلى قرارات حملات عالية التأثير.' },
].map((f, i) => ({ ...f, icon: FEATURE_ICONS[i] }));

const TABS = [
  {
    key: 'smart-cx',
    label: 'Smart CX Engine',
    heading: 'Smart CX Engine — برمجيات تجربة عملاء بالذكاء الاصطناعي تعمل فعلاً',
    lead: 'ذكاء اصطناعي يقدّم تفاعلات شخصية شبيهة بالبشر على نطاق واسع — يحسّن أوقات الاستجابة مع الحفاظ باستمرار على صوت علامتك التجارية.',
    features: SMART_CX_FEATURES,
  },
  {
    key: 'command-centre',
    label: 'Command Centre',
    heading: 'Command Centre — مركز قيادة رقمي لذكاء العلامة التجارية في الوقت الفعلي',
    lead: 'اكتشف الأزمات واحتوها في الوقت الفعلي — حماية سمعة علامتك التجارية قبل أن تتفاقم المشكلات.',
    features: COMMAND_CENTRE_FEATURES,
  },
  {
    key: 'social-listening',
    label: 'Social Listening Platform',
    heading: 'Social Listening Platform — مراقبة دائمة للعلامة التجارية',
    lead: 'تتوقّف معظم المنصات عند العربية الفصحى. Locobuzz يتجاوز ذلك — بمحرك NLP عربي متقدّم يتعرّف على اللهجات والفروق الثقافية، حتى تبدو علامتك محلية وليست أجنبية.',
    features: SOCIAL_LISTENING_FEATURES,
  },
];

const PUBLISHING = {
  heading: 'Publishing — مركز إدارة المحتوى متعدد القنوات',
  lead: 'خطّط للمحتوى ورتّبه في قائمة الانتظار ووافق عليه وانشره عبر جميع القنوات وجميع المواقع — من لوحة تحكم موحّدة واحدة. تتبّع الأداء يومياً وأسبوعياً وشهرياً.',
};

const DIALECTS = ['العربية الخليجية', 'الشامية', 'المصرية', 'الفصحى (MSA)', 'المغاربية'];

const LANG_BULLETS = [
  'محرك NLP عربي متقدم مع تعرّف على اللهجات',
  'تحليل مشاعر مضبوط على التعبير الاصطلاحي والثقافي العربي',
  'وعي بالسياق الثقافي — لا مجرد ترجمة حرفية',
  'تدفقات عمل ثنائية اللغة (عربي / إنجليزي) في واجهة واحدة',
  'دعم كامل لاتجاه الكتابة من اليمين إلى اليسار (RTL) في المنصة بأكملها',
];

const AI_FEATURES = [
  { title: 'Location Intelligence', img: 'ai-carousel/location-intelligence.webp', desc: 'يوحّد المراجعات والاستطلاعات والمحادثات في رؤية واحدة لكل موقع — يُبرز المشكلات، ويتيح ردوداً مدركة للسياق، ويساعد الفرق على التصرف مبكراً مع الحفاظ على الاتساق على نطاق واسع.' },
  { title: 'SignalSense™', img: 'ai-carousel/signalsense.webp', desc: 'يُبرز الإشارات عالية التأثير فقط — يقلّل الضوضاء ويرتّب أولوية التنبيهات حسب الشدة والانتشار إلى الفرق المناسبة للتصرف بسرعة واغتنام الزخم الناشئ مبكراً.' },
  { title: 'ContextualPulse™', img: 'ai-carousel/contextual.webp', desc: 'يصوغ الرد الصحيح في كل مرة — مخصّص وملتزم بصوت العلامة وبالنبرة المناسبة. يزوّد الوكلاء بنقطة انطلاق قوية للاستجابة بسرعة والبقاء متّسقين عبر التفاعلات.' },
  { title: 'ResponseGenie™', img: 'ai-carousel/responsegenie.webp', desc: 'يكتب الرد الصحيح في كل مرة — مخصّص وملتزم بصوت العلامة وبالنبرة المناسبة. يمنح كل وكيل نقطة انطلاق جاهزة للتحسين لردود أسرع وأكثر اتساقاً.' },
  { title: 'AgentIQ™', img: 'ai-carousel/agentiq.webp', desc: 'يعمل في الوقت الفعلي إلى جانب كل وكيل — يلتقط الردود المحفوفة بالمخاطر قبل إرسالها، ويقلّل إعادة فتح الحالات، ويحمي درجات CSAT.' },
  { title: 'أتمتة سير العمل', img: 'ai-carousel/workflow-automation.webp', desc: 'سرّع كفاءة الدعم بتدفقات عمل ذاتية تؤتمت الردود والإسنادات والاستطلاعات باستخدام محفّزات ذكية قائمة على الشروط.' },
];

const DANUBE_STATS = [
  { icon: 'icon-hourglass-v2.webp', value: '<1 Min', label: 'متوسط وقت الاستجابة عبر جميع القنوات' },
  { icon: 'icon-message-v2.webp', value: '100%', label: 'معدل نجاح الرد الآلي' },
  { icon: 'icon-question-v2.webp', value: 'صفر', label: 'استفسارات باردة دون إجابة' },
];

const OUTCOMES = [
  {
    icon: 'icon-heart-v2.webp', metric: '25 → 63%', label: 'ارتفاع في معدل الاستجابة',
    tag: 'بناء الولاء', industry: 'الرعاية الصحية',
    heading: 'شبكة رعاية صحية رائدة في الهند',
    vertical: 'الرعاية الصحية · 72 مستشفى',
    desc: 'تجربة المرضى والسمعة عبر 72 مستشفى وأكثر من 3 ملايين متابع. ردود حساسة مع تدخّل بشري، ولوحات أداء على مستوى كل مستشفى للقيادة.',
    modules: ['ContextualPulse™', 'AgentIQ™', 'ResponseGenie™'],
  },
  {
    icon: 'icon-airline-v2.webp', metric: '40% → 100%', label: 'حلول أسرع والتزام كامل باتفاقية مستوى الخدمة',
    tag: 'خفض تكلفة الخدمة', industry: 'الطيران',
    heading: 'شركة طيران كاملة الخدمات رائدة في الهند',
    vertical: 'الطيران · أكبر شركة طيران في الهند',
    desc: 'توحيد البريد الإلكتروني والتواصل الاجتماعي مع تكامل Salesforce CRM. أتمتة بالذكاء الاصطناعي لحلول أسرع وتنبيهات فورية للحالات الحرجة المتعلقة بالسلامة.',
    modules: ['Workflow Automation', 'ResponseGenie™', 'AgentIQ™'],
  },
  {
    icon: 'icon-bfsi-v2.webp', metric: '120M', label: 'محادثة يتم رصدها سنوياً',
    tag: 'الحماية على نطاق واسع', industry: 'BFSI',
    heading: 'أحد أكبر البنوك الخاصة في الهند',
    vertical: 'BFSI · أحد أكبر البنوك الخاصة في الهند.',
    desc: 'مركز قيادة رقمي يضم أكثر من 22 شاشة لعرض البيانات موزّعة على 3 مقار رئيسية. رؤية مباشرة وشاملة لعمليات التواصل الاجتماعي للإدارة العليا.',
    modules: ['SignalSense™', 'ContextualPulse™'],
  },
  {
    icon: 'icon-automobile-v2.webp', metric: '–87% → +32%', label: 'انخفاض زمن الاستجابة وارتفاع رضا العملاء',
    tag: 'التوسّع عبر الشبكة', industry: 'السيارات',
    heading: 'أكثر من 900 وكالة عبر الشبكة',
    vertical: 'السيارات · أكثر من 900 وكالة',
    desc: 'إدارة GMB وسمعة وسائل التواصل الاجتماعي على نطاق واسع عبر أكثر من 900 موقع وكالة. صوت آمن للعلامة، وتقارير MIS على مستوى الوكالة، ورؤية مباشرة من مقر المجموعة إلى صالة العرض.',
    modules: ['Workflow Automation', 'AgentIQ™', 'ResponseGenie™'],
  },
];

const PARTNER_POINTS = [
  { title: 'مُثبت على نطاق واسع', desc: 'موثوق من أكثر من 350 علامة تجارية عالمية عبر 4 قارات. لقد حللنا التحديات التي تواجهها، على نطاق واسع، من قبل.' },
  { title: 'موثوقية بمستوى المؤسسات', desc: 'مبني للعلامات التي لا تحتمل التوقف. أمان وقابلية توسّع وجاهزية بمستوى المؤسسات — حتى يعتمد عليه فريقك كل يوم.' },
  { title: 'في تحسّن مستمر', desc: 'الذكاء الاصطناعي أولاً ومعتمَد من Microsoft. قدرات جديدة تُطرح باستمرار. عملاؤنا يستفيدون أولاً.' },
];

const FAQS = [
  { q: 'هل يدعم Locobuzz اللهجات العربية إلى جانب الفصحى؟', a: 'نعم. يدعم Locobuzz واجهات RTL كاملة ومحرك NLP عربياً متقدماً يغطي الخليجية والشامية والمصرية والمغاربية والفصحى (MSA) — مع تحليل مشاعر مدرك للسياق الثقافي لكل لهجة.' },
  { q: 'ما المنصات الاجتماعية المغطّاة في الشرق الأوسط؟', a: 'إنستغرام وX (تويتر) وفيسبوك ولينكدإن ويوتيوب وتيك توك وواتساب وGoogle My Business، إضافة إلى الأخبار والمنتديات الإقليمية في المنطقة.' },
  { q: 'هل يتعامل Locobuzz مع نطاق المؤسسات والجهات الحكومية في المنطقة؟', a: 'نعم. المنصة منشورة لدى جهات حكومية ومشغّلي اتصالات وبنوك وشبكات رعاية صحية في المنطقة — وتتعامل مع ملايين التفاعلات شهرياً مع ضمانات SLA للمؤسسات.' },
  { q: 'هل يوجد دعم وتهيئة مخصّصان للشرق الأوسط؟', a: 'يوفّر Locobuzz تهيئة مخصّصة ومدير نجاح عملاء ودعماً مستمراً. تواصل عبر sales@locobuzz.com أو +91 22 4030 5500 للحديث مع فريق المنطقة.' },
  { q: 'ما شهادات الامتثال؟', a: 'متوافق مع ISO 27001:2022 وSOC 2 Type 2 وGDPR. كما يحظى Locobuzz باعتماد مايكروسوفت كمنصة تجربة عملاء تعتمد الذكاء الاصطناعي أولاً.' },
];

/* ── Page ───────────────────────────────────────────────────────────────── */
export default function Body() {
  const [activeTab, setActiveTab] = useState('smart-cx');
  const tab = TABS.find((t) => t.key === activeTab) ?? TABS[0];

  return (
    <>
      {/* ── 1 · HERO (light, centered) ──
          Reveal is CSS-only: framer-motion would ship `opacity: 0` in the
          static HTML and hold the LCP text unpainted until hydration. ── */}
      <section className="me-hero-light">
        <div className="me-hero-light-glow" aria-hidden="true" />
        <div className="me-hero-light-inner">
          <div className="me-hero-reveal">
            <span className="me-eyebrow">
              <MapPin size={13} /> الشرق الأوسط وشمال أفريقيا
            </span>
          </div>

          <h1 className="me-hero-light-title me-hero-reveal me-hero-reveal--1">
            <span className="me-hero-title-line">منصة إدارة تجربة العملاء</span>
            <span className="me-hero-title-line">
              التي يثق بها{' '}
              <span className="serif-accent italic" style={{ color: BRAND }}>الشرق الأوسط</span>
            </span>
          </h1>

          <p className="me-hero-light-lead me-hero-reveal me-hero-reveal--2">
            استمع إلى كل عميل، عبر كل قناة — بأكثر من 40 لغة ولهجة إقليمية. منصة تجربة عملاء موحَّدة واحدة تساعد العلامات التجارية على الاستجابة بشكل أسرع، والحلّ بشكل أذكى، والنمو بشكل أقوى — بصرف النظر عن مكان وجود عملائها.
          </p>

          <div className="me-hero-light-cta me-hero-reveal me-hero-reveal--3">
            <BookDemoButton size="lg" />
          </div>
        </div>

        <div className="me-hero-light-visual">
          <HeroCollage items={ME_HERO_COLLAGE} className="me-hero-collage" />
        </div>
      </section>

      {/* ── 2 · TRUSTED BY + LOGOS ── */}
      <section className={meStyles.trustSection}>
        <p className={meStyles.trustHeadline}>موثوق من أكثر من 350 شركة حول العالم</p>
        <MeLogoMarquee />
      </section>

      {/* ── 3 · METRICS ── */}
      <MetricsBand items={METRICS} />

      {/* ── 4 · THREE STEPS (text top, image bottom) ── */}
      <section style={{ padding: '88px 24px', background: 'var(--bg-page)' }}>
        <div style={{ maxWidth: 1120, margin: '0 auto' }}>
          <motion.div
            variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce} transition={spring}
            style={{ textAlign: 'center', marginBottom: 56, maxWidth: 760, marginInline: 'auto' }}
          >
            <h2 style={{ fontSize: 'clamp(1.6rem,3.2vw,2.4rem)', fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.3, marginBottom: 16 }}>
              من الإشارات الخامة إلى علاقات عملاء حقيقية — في الوقت الفعلي
            </h2>
            <p style={{ fontSize: 17, color: 'var(--text-muted)', lineHeight: 1.8 }}>
              لا تحتاج إلى عشرات الأدوات المنفصلة. Locobuzz منصة تجربة عملاء موحَّدة — حتى يقضي فريقك وقتاً أقل في التنقل بين الشاشات ووقتاً أكثر في بناء العلاقات.
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer(0.1)} initial="hidden" whileInView="show" viewport={viewportOnce}
            style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 28 }}
          >
            {STEPS.map(({ num, title, img, desc }) => (
              <motion.div
                key={num}
                variants={scaleIn} transition={springSoft}
                style={{
                  background: 'var(--bg-card)', border: '1px solid var(--border)',
                  borderRadius: 20, overflow: 'hidden', display: 'flex', flexDirection: 'column',
                }}
              >
                <div style={{ padding: '28px 26px', flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                    <span style={{ fontSize: 14, fontWeight: 800, color: BRAND }}>{num}</span>
                    <h3 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>{title}</h3>
                  </div>
                  <p style={{ fontSize: 15, color: 'var(--text-muted)', lineHeight: 1.8, margin: 0 }}>{desc}</p>
                </div>
                <img
                  src={`${IMG}/${img}`}
                  alt={`${title} — منصة Locobuzz لتجربة العملاء`}
                  loading="lazy"
                  decoding="async"
                  style={{ width: '100%', height: 200, objectFit: 'contain', objectPosition: 'center', display: 'block', background: 'var(--bg-page)' }}
                />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── 5 · BLUE UNIFIED PLATFORM BAND ── */}
      <section className="me-blue-band-section">
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce} transition={spring}
          className="me-blue-band"
        >
          <div className="me-blue-band-visual">
            <img
              src={`${IMG}/unified-platform-ui.webp`}
              alt="حارس الامتثال بالعربية يمنع رداً غير مناسب للعميل"
              loading="lazy"
              decoding="async"
              className="me-blue-band-img"
            />
          </div>
          <div className="me-blue-band-copy">
            <h2 style={{ fontSize: 'clamp(1.6rem,3.2vw,2.3rem)', fontWeight: 700, color: '#fff', lineHeight: 1.32, marginBottom: 18 }}>
              برنامج واحد لإدارة تجربة العملاء — لكل وظيفة تتعامل مع العميل.
            </h2>
            <p style={{ fontSize: 17, color: 'rgba(255,255,255,0.85)', lineHeight: 1.8, margin: 0 }}>
              كل تفاعلات عملائك، موحَّدة في منصة واحدة. يجمع Locobuzz الاستماع الاجتماعي ومراقبة العلامة التجارية وتفاعل العملاء وإدارة السمعة والذكاء الاصطناعي معاً في منصة تجربة عملاء واحدة — مبنية للتوسّع.
            </p>
          </div>
        </motion.div>
      </section>

      {/* ── 6 · SMART CX ENGINE (underline tabs) ── */}
      <section style={{ padding: '88px 24px 0', background: 'var(--bg-card)' }}>
        <div style={{ maxWidth: 980, margin: '0 auto' }}>
          <div className="me-tab-bar" role="tablist">
            {TABS.map((t) => {
              const active = t.key === activeTab;
              return (
                <button
                  key={t.key}
                  role="tab"
                  aria-selected={active}
                  onClick={() => setActiveTab(t.key)}
                  className={`me-tab${active ? ' me-tab--active' : ''}`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>

          <motion.div
            key={tab.key}
            variants={fadeUp} initial="hidden" animate="show" transition={spring}
            style={{ textAlign: 'center', marginBottom: tab.features.length ? 44 : 0, maxWidth: 760, marginInline: 'auto' }}
          >
            <h2 style={{ fontSize: 'clamp(1.5rem,3vw,2.2rem)', fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.32, marginBottom: 16 }}>
              {tab.heading}
            </h2>
            <p style={{ fontSize: 17, color: 'var(--text-muted)', lineHeight: 1.8 }}>{tab.lead}</p>
          </motion.div>

          {tab.features.length > 0 && (
            <motion.div
              key={`${tab.key}-features`}
              variants={staggerContainer(0.06)} initial="hidden" animate="show"
              style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}
            >
              {tab.features.map((f) => (
                <motion.div
                  key={f.title}
                  variants={scaleIn} transition={springSoft}
                  style={{
                    background: 'var(--bg-page)', border: '1px solid var(--border)',
                    borderRadius: 16, padding: '24px 22px', position: 'relative',
                  }}
                >
                  {f.icon && (
                    <img
                      src={`${IMG}/${f.icon}`}
                      alt=""
                      aria-hidden="true"
                      style={{ position: 'absolute', top: 20, insetInlineEnd: 20, width: 28, height: 28 }}
                      loading="lazy"
                      decoding="async"
                    />
                  )}
                  <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6, paddingInlineEnd: 36 }}>{f.title}</h3>
                  <p style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.7, margin: 0 }}>{f.desc}</p>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </section>

      {/* ── 6b · PUBLISHING ── */}
      <section className="me-publishing-section">
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce} transition={spring}
          className="me-publishing-inner"
        >
          <h2 className="me-publishing-heading">{PUBLISHING.heading}</h2>
          <p className="me-publishing-lead">{PUBLISHING.lead}</p>
        </motion.div>
      </section>

      {/* ── 7 · LANGUAGE & DIALECT (dark, full-bleed) ── */}
      <section className="me-dark-band-section">
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce} transition={spring}
          className="me-dark-band"
        >
          <div className="me-dark-band-visual">
            <img
              src={`${IMG}/language-dialect-v2.webp`}
              alt="لوحة الاستماع الاجتماعي العربي مع رؤى الإشارات حسب اللهجة"
              loading="lazy"
              decoding="async"
              className="me-dark-band-img"
            />
          </div>
          <div className="me-dark-band-copy">
            <h2 style={{ fontSize: 'clamp(1.5rem,3vw,2.2rem)', fontWeight: 700, color: '#fff', lineHeight: 1.32, marginBottom: 16 }}>
              افهم عملاءك وتفاعَل معهم بلغتهم ولهجتهم
            </h2>
            <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.78)', lineHeight: 1.8, marginBottom: 24 }}>
              دعم اللغة العربية الفصحى هو الحد الأدنى. يتجاوز Locobuzz الفصحى (MSA) ليفهم المحادثات عبر اللهجات الخليجية والشامية والمصرية والمغاربية — حتى لا تُساء قراءة أي إشارة ولا تُفَّوت أي مشاعر.
            </p>
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 28px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              {LANG_BULLETS.map((b) => (
                <li key={b} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 15, color: 'rgba(255,255,255,0.82)', lineHeight: 1.6 }}>
                  <Check size={16} style={{ color: BRAND, flexShrink: 0, marginTop: 3 }} />
                  {b}
                </li>
              ))}
            </ul>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', color: 'rgba(255,255,255,0.55)', marginBottom: 12 }}>
                تغطية اللهجات
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {DIALECTS.map((d) => (
                  <span key={d} style={{
                    padding: '7px 16px', borderRadius: 100, fontSize: 13, fontWeight: 600,
                    background: 'rgba(0,152,255,0.12)', border: '1px solid rgba(0,152,255,0.28)', color: BRAND,
                  }}>{d}</span>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* ── 8 · AI FEATURES CAROUSEL ── */}
      <MeAICarousel
        features={AI_FEATURES}
        imgBase={IMG}
        headerBadge="رواد الذكاء الاصطناعي الأوائل. معتمَدون من Microsoft."
        headerTitle="ذكاء اصطناعي يرفع مستوى كل تفاعل"
        headerDesc="يقلّل ذكاء Locobuzz الاصطناعي الضوضاء، ويحمي سمعتك، ويساعد كل وكيل على تقديم أفضل أداء — مصمَّم خصيصاً لتجربة العملاء."
        microsoftAlt="Locobuzz — رواد الذكاء الاصطناعي، معتمَدون من Microsoft"
      />

      {/* ── 9 · DANUBE / CONVERSION BAND ── */}
      <section className="me-conversion-band">
        <div className="me-conversion-inner">
          <motion.div
            variants={slideInRight} initial="hidden" whileInView="show" viewport={viewportOnce} transition={spring}
            className="me-conversion-stats"
          >
            {DANUBE_STATS.map((s) => (
              <div key={s.label} className="me-conversion-stat">
                <img src={`${IMG}/${s.icon}`} alt="" loading="lazy" decoding="async" style={{ width: 44, height: 44, flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: 30, fontWeight: 800, color: BRAND, lineHeight: 1.1 }}>{s.value}</div>
                  <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.72)', lineHeight: 1.5 }}>{s.label}</div>
                </div>
              </div>
            ))}
          </motion.div>

          <motion.div variants={slideInLeft} initial="hidden" whileInView="show" viewport={viewportOnce} transition={springSoft} className="me-conversion-copy">
            <img
              src={`${IMG}/danube-properties.svg`}
              alt="Danube Properties"
              loading="lazy"
              decoding="async"
              style={{ height: 40, width: 'auto', marginBottom: 24 }}
            />
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', color: BRAND, marginBottom: 8 }}>
              (قصة نجاح محلية)
            </div>
            <h2 style={{ fontSize: 'clamp(1.5rem,3vw,2.2rem)', fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.32, marginBottom: 20 }}>
              حيث تتحوّل المحادثات إلى صفقات
            </h2>
            <p style={{ fontSize: 18, color: 'var(--text-secondary)', lineHeight: 1.8, fontStyle: 'italic', marginBottom: 16 }}>
              «احتجنا إلى الاستجابة بشكل أسرع عبر وسائل التواصل الاجتماعي دون تفويت أي عميل محتمل. غيّر Locobuzz طريقة تعاملنا مع محادثات العملاء في قطاع العقارات.»
            </p>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-muted)' }}>
              Danube Properties | العقارات | الإمارات العربية المتحدة
            </div>
            <img
              src={`${IMG}/danube-house-3d.webp`}
              alt=""
              aria-hidden="true"
              loading="lazy"
              decoding="async"
              className="me-conversion-house"
            />
          </motion.div>
        </div>
      </section>

      {/* ── 10 · OUTCOMES CAROUSEL ── */}
      <MeOutcomesCarousel
        outcomes={OUTCOMES}
        imgBase={IMG}
        title="أربع نتائج. أربعة عملاء محددين. أرقام حقيقية."
      />

      {/* ── 11 · PARTNER (Figma trust banner + 3 cards) ── */}
      <section className="me-partner-section">
        <div style={{ maxWidth: 1120, margin: '0 auto', position: 'relative' }}>
          <MeTrustBanner
            imgBase={IMG}
            line1="لا تختر منصة فحسب."
            line2="اختر شريكاً يمكنك الوثوق به."
          />

          <motion.div
            variants={staggerContainer(0.1)} initial="hidden" whileInView="show" viewport={viewportOnce}
            style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 24 }}
          >
            {PARTNER_POINTS.map((p) => (
              <motion.div
                key={p.title}
                variants={scaleIn} transition={springSoft}
                style={{
                  background: 'var(--bg-card)', border: '1px solid var(--border)',
                  borderRadius: 18, padding: '30px 26px',
                }}
              >
                <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 10 }}>{p.title}</h3>
                <p style={{ fontSize: 15, color: 'var(--text-muted)', lineHeight: 1.8, margin: 0 }}>{p.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section style={{ padding: '80px 24px', background: 'var(--bg-page)' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(1.25rem,2.5vw,1.75rem)', fontWeight: 700, color: 'var(--text-primary)', textAlign: 'center', marginBottom: 40 }}>
            الأسئلة الشائعة
          </h2>
          <FaqAccordion items={FAQS} columns={2} />
        </div>
      </section>

      {/* ── 12 · FINAL CTA ── */}
      <BookDemoSection formEager={false} />

      <section style={{ padding: '40px 24px 64px', background: 'var(--bg-page)', textAlign: 'center' }}>
        <p dir="ltr" lang="en" style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-secondary)' }}>
          #ShapingExperiences, one interaction at a time&trade;
        </p>
      </section>

      <InlineStyle css={`
        .me-hero-light {
          position: relative;
          overflow: visible;
          padding: 96px 24px 160px;
          background-color: var(--bg-page);
          background-image:
            radial-gradient(circle at 1px 1px, rgba(0, 152, 255, 0.07) 1px, transparent 0);
          background-size: 28px 28px;
          text-align: center;
        }
        .me-hero-light-glow {
          position: absolute;
          inset: 0;
          background:
            radial-gradient(ellipse 50% 40% at 15% 10%, rgba(0,152,255,0.12) 0%, transparent 70%),
            radial-gradient(ellipse 50% 40% at 85% 10%, rgba(0,152,255,0.10) 0%, transparent 70%);
          pointer-events: none;
        }
        .me-hero-light-inner {
          position: relative;
          max-width: var(--max-content);
          margin: 0 auto;
        }
        .me-hero-reveal {
          animation: meHeroReveal 0.5s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .me-hero-reveal--1 { animation-delay: 0.04s; }
        .me-hero-reveal--2 { animation-delay: 0.08s; }
        .me-hero-reveal--3 { animation-delay: 0.12s; }
        @keyframes meHeroReveal {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @media (prefers-reduced-motion: reduce) {
          .me-hero-reveal { animation: none; }
        }
        .me-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: ${BRAND_SUBTLE};
          border: 1px solid ${BRAND_BORDER};
          color: ${BRAND};
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.06em;
          padding: 6px 16px;
          border-radius: 100px;
          margin-bottom: 24px;
        }
        .me-hero-light-title {
          font-size: clamp(2rem,4.4vw,3.1rem);
          font-weight: 800;
          color: var(--text-primary);
          line-height: 1.25;
          margin-bottom: 18px;
        }
        .me-hero-title-line {
          display: block;
        }
        .me-hero-light-lead {
          font-size: 18px;
          color: var(--text-muted);
          line-height: 1.8;
          margin-bottom: 32px;
          max-width: 640px;
          margin-inline: auto;
        }
        .me-hero-light-cta { margin-bottom: 0; }
        .me-hero-light-visual {
          position: relative;
          width: min(var(--max-wide), calc(100% - 48px));
          max-width: none;
          margin: 48px auto 0;
          padding-bottom: 80px;
          min-height: clamp(180px, 28vw, 320px);
        }
        .me-hero-collage {
          width: 100%;
          max-width: none;
          margin: 0 auto;
        }
        .me-hero-collage [data-piece="dashboard"] {
          overflow: hidden;
          border-radius: 14px;
          background: var(--bg-page);
          box-shadow: 0 14px 32px rgba(15, 23, 42, 0.10);
        }
        .me-hero-collage [data-piece="dashboard"] img {
          width: 101%;
          height: auto;
          margin: -0.5% 0 0 -0.5%;
          filter: none;
        }
        @media (max-width: 900px) {
          .me-hero-light-visual {
            width: calc(100% - 32px);
            margin-top: 32px;
            padding-bottom: 24px;
            min-height: 0;
          }
        }
        .me-blue-band-section {
          padding: 0 0 88px;
          background: var(--bg-page);
        }
        .me-blue-band {
          display: grid;
          grid-template-columns: minmax(0, 1.15fr) minmax(0, 0.85fr);
          gap: clamp(12px, 2vw, 24px);
          align-items: center;
          width: 100%;
          margin: 0;
          padding: clamp(56px, 7vw, 88px) clamp(24px, 6vw, 96px);
          border-radius: 0;
          background: ${BRAND};
        }
        .me-blue-band-visual {
          display: flex;
          justify-content: flex-end;
          align-items: center;
        }
        .me-blue-band-img {
          width: 100%;
          max-width: min(760px, 100%);
          height: auto;
          display: block;
        }
        .me-blue-band-copy {
          max-width: 520px;
        }
        .me-dark-band-section {
          padding: 0 0 40px;
          background: var(--bg-card);
        }
        .me-dark-band {
          display: grid;
          grid-template-columns: minmax(0, 1.15fr) minmax(0, 0.85fr);
          gap: clamp(12px, 2vw, 24px);
          align-items: center;
          width: 100%;
          margin: 0;
          padding: clamp(56px, 7vw, 88px) clamp(24px, 6vw, 96px);
          border-radius: 0;
          background: ${BG_DARK};
        }
        .me-dark-band-visual {
          display: flex;
          justify-content: flex-end;
          align-items: center;
          overflow: visible;
        }
        .me-dark-band-img {
          width: 100%;
          max-width: min(760px, 100%);
          height: auto;
          display: block;
          border-radius: 0;
        }
        .me-dark-band-copy {
          max-width: 520px;
        }
        .me-tab-bar {
          display: flex;
          flex-wrap: wrap;
          gap: 0;
          justify-content: center;
          border-bottom: 1px solid var(--border);
          margin-bottom: 44px;
        }
        .me-tab {
          padding: 12px 24px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          background: none;
          border: none;
          border-bottom: 2px solid transparent;
          color: var(--text-muted);
          transition: color 0.2s, border-color 0.2s;
          margin-bottom: -1px;
        }
        .me-tab--active {
          /* --brand on white is only 3:1; the darker token clears 4.5:1 for text */
          color: var(--brand-dark);
          border-bottom-color: ${BRAND};
        }
        .me-publishing-section {
          padding: 28px 24px 88px;
          background: var(--bg-card);
        }
        .me-publishing-inner {
          max-width: 820px;
          margin: 0 auto;
          text-align: center;
          padding: clamp(40px, 5vw, 56px) clamp(28px, 4vw, 48px);
          border-radius: 24px;
          background: var(--bg-page);
          border: 1px solid var(--border);
          box-shadow: 0 8px 32px rgba(15, 23, 42, 0.04);
        }
        .me-publishing-heading {
          font-size: clamp(1.5rem, 3vw, 2.2rem);
          font-weight: 700;
          color: var(--text-primary);
          line-height: 1.28;
          margin: 0 0 16px;
        }
        .me-publishing-lead {
          font-size: 17px;
          color: var(--text-muted);
          line-height: 1.7;
          margin: 0;
          max-width: 680px;
          margin-inline: auto;
        }
        .me-conversion-band {
          padding: 88px 24px;
          background: linear-gradient(135deg, rgba(236,72,153,0.08) 0%, rgba(0,152,255,0.10) 100%);
        }
        .me-conversion-inner {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 48px;
          align-items: center;
          max-width: 1120px;
          margin: 0 auto;
        }
        .me-conversion-stats {
          display: flex;
          flex-direction: column;
          gap: 16px;
          background: ${BG_DARK};
          border-radius: 24px;
          padding: 32px 28px;
        }
        .me-conversion-stat {
          display: flex;
          align-items: center;
          gap: 18px;
        }
        .me-conversion-copy { position: relative; }
        .me-conversion-house {
          position: absolute;
          bottom: -20px;
          insetInlineStart: -10px;
          width: 120px;
          height: auto;
          opacity: 0.15;
          pointer-events: none;
        }
        .me-partner-section {
          padding: 88px 24px;
          background: var(--surface-sectionAlt, #F4F6FD);
          position: relative;
          overflow: hidden;
        }
        @media (max-width: 880px) {
          .me-blue-band,
          .me-dark-band,
          .me-conversion-inner { grid-template-columns: 1fr; gap: 32px; }
          .me-conversion-house { display: none; }
        }
      `} />
    </>
  );
}
